import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { faultsAPI, jobsAPI } from '../utils/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Eye,
  Trash2,
  X,
  Clock,
  CheckCircle2,
  Loader2,
  Phone,
  Mail,
  MapPin,
  User,
  FileText,
  Printer,
  Building2,
  RefreshCw,
  LayoutGrid,
  List,
} from 'lucide-react';

const FaultLog = () => {
  const { user } = useAuth();
  const [faults, setFaults] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [jobSearchFilter, setJobSearchFilter] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFault, setSelectedFault] = useState(null);
  
  // Create form state
  const [formData, setFormData] = useState({
    date_of_complaint: new Date().toISOString().split('T')[0],
    nature_of_complaint: '',
    job_id: '',
    is_manual_entry: false,
    client_name: '',
    client_location: '',
    client_contact_no: '',
    client_email: '',
    job_code: '',
    job_title: '',
    job_type_display: '',
    manager_name: '',
    status: 'pending',
    notes: '',
  });
  
  const [saving, setSaving] = useState(false);
  const printRef = useRef();

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [faultsRes, jobsRes, typesRes] = await Promise.all([
        faultsAPI.getAll({ status: statusFilter || undefined }),
        jobsAPI.getAll(),
        faultsAPI.getComplaintTypes(),
      ]);
      setFaults(faultsRes.data);
      setJobs(jobsRes.data);
      setComplaintTypes(typesRes.data);
    } catch (error) {
      toast.error('Failed to load fault logs');
    } finally {
      setLoading(false);
    }
  };

  const handleJobSelect = (jobId) => {
    if (!jobId) {
      setFormData(prev => ({
        ...prev,
        job_id: '',
        client_name: '',
        client_location: '',
        client_contact_no: '',
        client_email: '',
        job_type_display: '',
        manager_name: '',
      }));
      return;
    }
    
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      let jobTypeDisplay = 'Private';
      if (job.job_type !== 'private') {
        jobTypeDisplay = `Insurer – ${job.insurer_name || 'N/A'}. Loss Adjustor – ${job.loss_adjuster_name || 'N/A'}`;
      }
      
      setFormData(prev => ({
        ...prev,
        job_id: jobId,
        client_name: job.client_name || '',
        client_location: job.location || '',
        client_contact_no: job.client_contact || '',
        client_email: job.client_email || '',
        job_type_display: jobTypeDisplay,
        manager_name: job.manager_name || '',
      }));
    }
  };

  const handleCreateFault = async () => {
    if (!formData.nature_of_complaint) {
      toast.error('Please select a complaint type');
      return;
    }
    
    if (!formData.is_manual_entry && !formData.job_id) {
      toast.error('Please select a job or enable manual entry');
      return;
    }
    
    if (formData.is_manual_entry && !formData.client_name) {
      toast.error('Client name is required for manual entry');
      return;
    }
    
    try {
      setSaving(true);
      await faultsAPI.create(formData);
      toast.success('Fault logged successfully');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fault log');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFault = async (faultId, updateData) => {
    try {
      await faultsAPI.update(faultId, updateData);
      toast.success('Fault updated successfully');
      loadData();
      if (selectedFault && selectedFault.id === faultId) {
        setSelectedFault({ ...selectedFault, ...updateData });
      }
    } catch (error) {
      toast.error('Failed to update fault');
    }
  };

  const handleDeleteFault = async (faultId) => {
    if (!window.confirm('Are you sure you want to delete this fault log?')) return;
    
    try {
      await faultsAPI.delete(faultId);
      toast.success('Fault deleted successfully');
      loadData();
      if (selectedFault?.id === faultId) {
        setShowDetailModal(false);
        setSelectedFault(null);
      }
    } catch (error) {
      toast.error('Failed to delete fault');
    }
  };

  const resetForm = () => {
    setFormData({
      date_of_complaint: new Date().toISOString().split('T')[0],
      nature_of_complaint: '',
      job_id: '',
      is_manual_entry: false,
      client_name: '',
      client_location: '',
      client_contact_no: '',
      client_email: '',
      job_code: '',
      job_title: '',
      job_type_display: '',
      manager_name: '',
      status: 'pending',
      notes: '',
    });
  };

  const handlePrint = () => {
    const printContent = document.getElementById('print-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Fault Log Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f5f5f5; }
            h1 { font-size: 18px; margin-bottom: 5px; }
            .date { font-size: 12px; color: #666; margin-bottom: 20px; }
            .status-pending { color: #f59e0b; }
            .status-in_progress { color: #3b82f6; }
            .status-resolved { color: #10b981; }
          </style>
        </head>
        <body>
          <h1>Fault Log Report</h1>
          <p class="date">Generated: ${format(new Date(), 'PPpp')}</p>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
    in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: RefreshCw },
    resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  };

  // Filter faults by job search
  const filteredFaults = faults.filter(fault => {
    if (!jobSearchFilter) return true;
    const search = jobSearchFilter.toLowerCase();
    return (
      fault.job_code?.toLowerCase().includes(search) ||
      fault.job_title?.toLowerCase().includes(search) ||
      fault.client_name?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (!canEdit) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-700">Access Denied</h2>
          <p className="text-red-600">Only administrators and managers can access the fault log.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fault Log</h1>
          <p className="text-slate-600">Track and manage complaints and issues</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
              title="Grid View"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Log Fault
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={jobSearchFilter}
              onChange={(e) => setJobSearchFilter(e.target.value)}
              placeholder="Search by job code, title, or client..."
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div className="text-sm text-slate-500">
            {filteredFaults.length} fault{filteredFaults.length !== 1 ? 's' : ''} found
          </div>
        </div>
      </div>

      {/* Faults Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredFaults.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-slate-600">No faults found</h3>
          <p className="text-slate-500">
            {statusFilter || jobSearchFilter ? 'Try adjusting your filters' : 'Click "Log Fault" to create the first entry'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFaults.map((fault) => {
            const config = statusConfig[fault.status] || statusConfig.pending;
            const StatusIcon = config.icon;

            return (
              <div
                key={fault.id}
                onClick={() => {
                  setSelectedFault(fault);
                  setShowDetailModal(true);
                }}
                className="bg-white border border-slate-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-mono font-bold text-orange-600">
                    {fault.fault_code}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {config.label}
                  </span>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-1 text-sm">
                  {fault.job_code ? `${fault.job_code} – ${fault.job_title}` : fault.job_title || 'Manual Entry'}
                </h3>
                
                <div className="text-sm text-slate-600 mb-3">
                  <p className="font-medium">{fault.client_name || 'N/A'}</p>
                  {fault.client_location && (
                    <p className="text-xs text-slate-500 truncate">{fault.client_location}</p>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <AlertTriangle className="w-3 h-3 text-orange-500" />
                  <span className="truncate">{fault.nature_of_complaint}</span>
                </div>
                
                <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
                  <span>Complaint: {formatDate(fault.date_of_complaint)}</span>
                  {fault.client_contact_no && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {fault.client_contact_no}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Fault ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Job Code – Title</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Location</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Nature</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredFaults.map((fault) => {
                const config = statusConfig[fault.status] || statusConfig.pending;
                const StatusIcon = config.icon;

                return (
                  <tr 
                    key={fault.id} 
                    onClick={() => {
                      setSelectedFault(fault);
                      setShowDetailModal(true);
                    }}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono font-bold text-orange-600">{fault.fault_code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">
                          {fault.job_code ? `${fault.job_code} – ${fault.job_title}` : fault.job_title || 'Manual Entry'}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{fault.client_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[150px] truncate">{fault.client_location || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{fault.client_contact_no || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDate(fault.date_of_complaint)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[150px] truncate">{fault.nature_of_complaint}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {config.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Hidden print content */}
      <div id="print-content" className="hidden">
        <table>
          <thead>
            <tr>
              <th>Fault Code</th>
              <th>Job Code – Title</th>
              <th>Client Name</th>
              <th>Location</th>
              <th>Contact No</th>
              <th>Date of Complaint</th>
              <th>Nature of Complaint</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredFaults.map((fault) => (
              <tr key={fault.id}>
                <td>{fault.fault_code}</td>
                <td>{fault.job_code ? `${fault.job_code} – ${fault.job_title}` : fault.job_title || '-'}</td>
                <td>{fault.client_name || '-'}</td>
                <td>{fault.client_location || '-'}</td>
                <td>{fault.client_contact_no || '-'}</td>
                <td>{formatDate(fault.date_of_complaint)}</td>
                <td>{fault.nature_of_complaint}</td>
                <td className={`status-${fault.status}`}>{fault.status?.replace('_', ' ')}</td>
                <td>{fault.notes || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Fault Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Log New Fault
              </h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Date of Complaint */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date of Complaint</label>
                <input
                  type="date"
                  value={formData.date_of_complaint}
                  onChange={(e) => setFormData({ ...formData, date_of_complaint: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Nature of Complaint */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nature of Complaint *</label>
                <select
                  value={formData.nature_of_complaint}
                  onChange={(e) => setFormData({ ...formData, nature_of_complaint: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select complaint type...</option>
                  {complaintTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Manual Entry Toggle */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="manual_entry"
                  checked={formData.is_manual_entry}
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      is_manual_entry: e.target.checked,
                      job_id: e.target.checked ? '' : formData.job_id,
                    });
                  }}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="manual_entry" className="text-sm text-slate-700">
                  Manual entry for old job not in system
                </label>
              </div>

              {/* Job Selection (if not manual) */}
              {!formData.is_manual_entry && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Job *</label>
                  <select
                    value={formData.job_id}
                    onChange={(e) => handleJobSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Search and select a job...</option>
                    {jobs.map((job) => (
                      <option key={job.id} value={job.id}>
                        {job.job_code} – {job.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Manual Entry Fields */}
              {formData.is_manual_entry && (
                <div className="space-y-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
                  <p className="text-sm font-medium text-slate-600">Manual Job Entry</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Job Code</label>
                      <input
                        type="text"
                        value={formData.job_code}
                        onChange={(e) => setFormData({ ...formData, job_code: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                        placeholder="e.g., MBC25001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Job Title</label>
                      <input
                        type="text"
                        value={formData.job_title}
                        onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                        placeholder="Job title"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Auto-populated or Manual Client Info */}
              <div className="space-y-4">
                <p className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Client Information {!formData.is_manual_entry && formData.job_id && '(Auto-populated)'}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Client Name *</label>
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      disabled={!formData.is_manual_entry && formData.job_id}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contact Number</label>
                    <input
                      type="text"
                      value={formData.client_contact_no}
                      onChange={(e) => setFormData({ ...formData, client_contact_no: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      disabled={!formData.is_manual_entry && formData.job_id}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      disabled={!formData.is_manual_entry && formData.job_id}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Location/Address</label>
                    <input
                      type="text"
                      value={formData.client_location}
                      onChange={(e) => setFormData({ ...formData, client_location: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      disabled={!formData.is_manual_entry && formData.job_id}
                    />
                  </div>
                </div>
                
                {formData.is_manual_entry && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Job Type</label>
                      <input
                        type="text"
                        value={formData.job_type_display}
                        onChange={(e) => setFormData({ ...formData, job_type_display: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                        placeholder="e.g., Private or Insurer – X"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Manager/Supervisor</label>
                      <input
                        type="text"
                        value={formData.manager_name}
                        onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                        placeholder="Manager name"
                      />
                    </div>
                  </div>
                )}
                
                {!formData.is_manual_entry && formData.job_id && formData.job_type_display && (
                  <div className="text-sm text-slate-600 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>Job Type: {formData.job_type_display}</span>
                  </div>
                )}
                
                {!formData.is_manual_entry && formData.job_id && formData.manager_name && (
                  <div className="text-sm text-slate-600 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>Manager: {formData.manager_name}</span>
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Insurer Complaints Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter any notes about the complaint..."
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFault}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Log Fault
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedFault && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <span className="text-orange-600 font-mono">{selectedFault.fault_code}</span>
                </h2>
                <p className="text-sm text-slate-600">
                  {selectedFault.job_code ? `${selectedFault.job_code} – ${selectedFault.job_title}` : selectedFault.job_title || 'Manual Entry'}
                </p>
              </div>
              <button onClick={() => { setShowDetailModal(false); setSelectedFault(null); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Update */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select
                    value={selectedFault.status}
                    onChange={(e) => handleUpdateFault(selectedFault.id, { status: e.target.value })}
                    className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <button
                  onClick={() => handleDeleteFault(selectedFault.id)}
                  className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>

              {/* Complaint Details */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">{selectedFault.nature_of_complaint}</p>
                    <p className="text-sm text-slate-600">Date of complaint: {formatDate(selectedFault.date_of_complaint)}</p>
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Client:</span>
                    <span className="font-medium text-slate-900">{selectedFault.client_name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Location:</span>
                    <span className="text-slate-900">{selectedFault.client_location || 'N/A'}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Contact:</span>
                    <span className="text-slate-900">{selectedFault.client_contact_no || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Email:</span>
                    <span className="text-slate-900">{selectedFault.client_email || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Job Type & Manager */}
              <div className="grid grid-cols-2 gap-4 py-4 border-t border-slate-200">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Job Type:</span>
                  <span className="text-slate-900">{selectedFault.job_type_display || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Manager:</span>
                  <span className="text-slate-900">{selectedFault.manager_name || 'N/A'}</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Insurer Complaints Notes
                </label>
                <textarea
                  value={selectedFault.notes || ''}
                  onChange={(e) => {
                    setSelectedFault({ ...selectedFault, notes: e.target.value });
                  }}
                  onBlur={(e) => {
                    if (e.target.value !== selectedFault.notes) {
                      handleUpdateFault(selectedFault.id, { notes: e.target.value });
                    }
                  }}
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter notes..."
                />
              </div>

              {/* Metadata */}
              <div className="text-xs text-slate-500 pt-4 border-t border-slate-200 flex items-center justify-between">
                <span>Created by: {selectedFault.created_by_name} on {formatDate(selectedFault.created_at)}</span>
                {selectedFault.resolved_at && (
                  <span className="text-green-600">Resolved: {formatDate(selectedFault.resolved_at)}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaultLog;
